import { useState } from 'react';
import { motion } from 'framer-motion';

const Projects = () => {
  const [filter, setFilter] = useState('all');

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'residential', name: 'Residential' },
    { id: 'commercial', name: 'Commercial' },
    { id: 'office', name: 'Office' },
    { id: 'hospitality', name: 'Hospitality' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Luxury Villa Interior',
      category: 'residential',
      location: 'Visakhapatnam',
      image: 'https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Complete interior fitout with custom furniture'
    },
    {
      id: 2,
      title: 'Corporate Office Space',
      category: 'office',
      location: 'Hyderabad',
      image: 'https://images.unsplash.com/photo-1770425616829-61d6912705ad?q=80&w=2070&auto=format&fit=crop',
      description: 'Modern office furniture and workstations'
    },
    {
      id: 3,
      title: 'Premium Residence',
      category: 'residential',
      location: 'Bangalore',
      image: 'https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Modular kitchen and wardrobe systems'
    },
    {
      id: 4,
      title: 'Retail Showroom',
      category: 'commercial',
      location: 'Chennai',
      image: 'https://images.pexels.com/photos/31570248/pexels-photo-31570248.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Custom display units and interiors'
    },
    {
      id: 5,
      title: 'Boutique Hotel',
      category: 'hospitality',
      location: 'Goa',
      image: 'https://images.unsplash.com/photo-1770425616829-61d6912705ad?q=80&w=2070&auto=format&fit=crop',
      description: 'Complete furniture and interior solution'
    },
    {
      id: 6,
      title: 'Executive Office',
      category: 'office',
      location: 'Mumbai',
      image: 'https://images.unsplash.com/photo-1770425616829-61d6912705ad?q=80&w=2070&auto=format&fit=crop',
      description: 'Premium office furniture and fitout'
    },
    {
      id: 7,
      title: 'Modern Apartment',
      category: 'residential',
      location: 'Pune',
      image: 'https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Full home interior design'
    },
    {
      id: 8,
      title: 'Restaurant Interior',
      category: 'hospitality',
      location: 'Delhi',
      image: 'https://images.unsplash.com/photo-1770425616829-61d6912705ad?q=80&w=2070&auto=format&fit=crop',
      description: 'Custom dining furniture and ambiance'
    },
    {
      id: 9,
      title: 'Shopping Complex',
      category: 'commercial',
      location: 'Visakhapatnam',
      image: 'https://images.pexels.com/photos/31570248/pexels-photo-31570248.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Turnkey commercial interior project'
    }
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter);

  return (
    <div className="min-h-screen pt-20" data-testid="projects-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="projects-hero">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Portfolio</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              Projects that Inspire.<br />
              Quality that Endures.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl">
              Explore our portfolio of completed projects across residential, commercial, office, and hospitality sectors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-slate-200 sticky top-20 bg-white z-40" data-testid="project-filters">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                data-testid={`filter-${category.id}`}
                className={`px-6 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                  filter === category.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Masonry Grid */}
      <section className="py-16" data-testid="projects-grid">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="break-inside-avoid group"
                data-testid={`project-${project.id}`}
              >
                <div className="bg-white border border-slate-200 overflow-hidden transition-all duration-500 hover:shadow-lg">
                  <div className="relative overflow-hidden" style={{ height: `${250 + (index % 3) * 100}px` }}>
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/20 transition-colors duration-500" />
                  </div>
                  <div className="p-6">
                    <p className="text-xs font-bold uppercase tracking-widest text-bronze-500 mb-2">{project.location}</p>
                    <h3 className="text-xl font-medium mb-2">{project.title}</h3>
                    <p className="text-muted-foreground text-sm">{project.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-50" data-testid="projects-cta">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Let's Create Your Next Project
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-8 max-w-2xl mx-auto">
              Whether it's a residential interior or a commercial fitout, we're ready to bring your vision to life.
            </p>
            <a 
              href="/contact" 
              data-testid="projects-contact-button"
              className="inline-flex items-center bg-slate-900 text-white hover:bg-slate-800 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all"
            >
              Start Your Project
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
